import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { login } from "../../../data";
import AuthStore from "../../../stores/AuthStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { AuthLayout } from "../shared/layout";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { BASEAPIURL } from "../../../data/api/constants";

export function LoginScreen() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const {
    mutate: loginUser,
    isLoading,
  } = useMutation({
    mutationKey: "login",
    mutationFn: (data) => login(data),
    onSuccess: (response) => {
      AuthStore.setUser(response.data.token);
      navigate("/dashboard");
      toast({
        title: t("auth.login.successTitle"),
        description: t("auth.login.successDescription"),
      });
    },
    onError: (error) => {
      const errorCode = error.response?.data?.code;
      const localizedError = errorCode === "INVALID_CREDENTIALS"
        ? t("auth.login.invalidCredentials")
        : errorCode === "AUTH_FIELDS_REQUIRED"
          ? t("auth.login.fieldsRequired")
          : null;
      toast({
        title: t("common.error"),
        description: localizedError || t("auth.login.failed"),
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (token) {
      AuthStore.setUser(token);
      navigate("/dashboard");
    }
  }, [location, navigate]);

  const onSubmit = (data) => {
    loginUser(data);
  };

  const handleGoogleLogin = () => {
    const apiUrl = (process.env.REACT_APP_API_URL || BASEAPIURL).replace(/\/$/, "");
    const returnUrl = `${window.location.origin}/login`;
    window.location.href = `${apiUrl}/auth/google?redirect=${encodeURIComponent(returnUrl)}`;
  };

  return (
    <AuthLayout
      title={t("auth.login.title")}
      description={t("auth.login.description")}
    >
      <div className="grid gap-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Controller
                name="email"
                control={control}
                rules={{ required: t("auth.validation.emailRequired") }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    placeholder="name@example.com"
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                  />
                )}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-muted-foreground hover:underline"
                >
                  {t("auth.login.forgotPassword")}
                </Link>
              </div>
              <div className="relative">
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: t("auth.validation.passwordRequired") }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="password"
                      type={showPassword ? "text" : "password"}
                      disabled={isLoading}
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
            </div>
            <Button disabled={isLoading}>
              {isLoading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              {t("auth.login.signInWithEmail")}
            </Button>
          </div>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t("auth.orContinueWith")}
            </span>
          </div>
        </div>

        <Button variant="outline" type="button" disabled={isLoading} onClick={handleGoogleLogin}>
          {isLoading ? (
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <GoogleMark />
          )}
          Google
        </Button>

        <p className="px-8 text-center text-sm text-muted-foreground">
          {t("auth.login.noAccount")}{" "}
          <Link
            to="/register"
            className="underline underline-offset-4 hover:text-primary"
          >
            {t("auth.login.signUp")}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

function GoogleMark() {
  return (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.36l-3.24-2.54c-.9.6-2.05.96-3.38.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.93A6 6 0 0 1 6.08 12c0-.67.11-1.32.31-1.93V7.45H3.04A10 10 0 0 0 2 12c0 1.61.38 3.14 1.04 4.55l3.35-2.62Z" />
      <path fill="#EA4335" d="M12 5.94c1.47 0 2.79.5 3.82 1.49l2.87-2.87A9.65 9.65 0 0 0 12 2a10 10 0 0 0-8.96 5.45l3.35 2.62C7.18 7.7 9.39 5.94 12 5.94Z" />
    </svg>
  );
}
